// This Month build-up (Jacky, 23 October 2026: "a graph of each type growing up to potential buildup of revenue and
// profit"). Four steps, each stacked on the one before: Recognised → + Scheduled → + Pending schedule → + Opportunity.
// Each step shows two bars, revenue and profit; the faint part is what earlier steps built, the solid part is what this
// step adds. Profit counts priced work only — a Job with no cost adds revenue and is counted under the step instead.
// Opportunity is not won, so it is drawn lighter. The dashed line is the saved monthly fixed-cost target. Profit that
// still rests on the acceptance estimate (catalogue cost, standard labour rates; 23 Oct 2026) is drawn hatched on top of
// the confirmed part, so the chart shows how much of the potential is proven.
import type { MonthlyPerformance } from "@aiw/api-client";
import { aud } from "../lib/format";
import { useT } from "../lib/i18n";

type Step = MonthlyPerformance["build_up"][number];

const W = 640, H = 260, TOP = 22, BOTTOM = 196, BAR = 46, GAP = 8;
const HATCH = "repeating-linear-gradient(45deg, currentColor 0 3px, transparent 3px 6px)";

export function MonthBuildUp({ steps, fixedCostTarget }: { steps: Step[]; fixedCostTarget: number }) {
  const { t } = useT();
  if (!steps.length) return null;
  const max = Math.max(steps.at(-1)!.cumulative_revenue, fixedCostTarget, 1) * 1.08;
  const y = (v: number) => BOTTOM - (Math.max(0, v) / max) * (BOTTOM - TOP);
  const slot = W / steps.length;
  const summary = steps.map((s) => `${t(`month.buildUp.${s.key}`)} ${aud(s.cumulative_revenue)} / ${aud(s.cumulative_profit_known)}`).join(" · ");
  const bar = (x: number, before: number, after: number, solid: string, light: boolean, title: string, estimated = 0) => (
    <g>
      <rect x={x} y={y(before)} width={BAR} height={BOTTOM - y(before)} rx={2} className="fill-muted" />
      <rect x={x} y={y(after - estimated)} width={BAR} height={Math.max(0, y(before) - y(after - estimated))} rx={2} className={solid} opacity={light ? 0.45 : 1}>
        <title>{title}</title>
      </rect>
      {estimated > 0 && <rect x={x} y={y(after)} width={BAR} height={Math.max(0, y(after - estimated) - y(after))} rx={2} fill="url(#month-est-hatch)" opacity={light ? 0.6 : 1}>
        <title>{`${t("month.buildUpEstimated")} ${aud(estimated)}`}</title>
      </rect>}
      <text x={x + BAR / 2} y={y(after) - 5} textAnchor="middle" className="fill-foreground num" fontSize={10}>{aud(after)}</text>
    </g>
  );
  return (
    <div>
      <ol className="space-y-3 sm:hidden" aria-label={t("month.buildUpAria", { summary })}>
        {steps.map((s, i) => {
          const pct = (v: number) => `${Math.max(0, (v / max) * 100)}%`;
          const light = s.key === "opportunity" ? "opacity-45" : "";
          const row = (before: number, after: number, solid: string, label: string, estimated = 0) => (
            <div className="flex items-center gap-2">
              <div className="flex h-3 flex-1 overflow-hidden rounded-sm bg-card" aria-hidden="true">
                <span className="h-full bg-muted" style={{ width: pct(before) }} />
                <span className={`h-full ${solid} ${light}`} style={{ width: pct(after - before - estimated) }} />
                {estimated > 0 && <span className={`h-full text-success ${light}`} style={{ width: pct(estimated), backgroundImage: HATCH }} />}
              </div>
              <span className="num w-20 text-right text-micro">{aud(after)}</span>
              <span className="sr-only">{label}</span>
            </div>
          );
          return (
            <li key={s.key}>
              <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                <strong>{i === 0 ? "" : "+ "}{t(`month.buildUp.${s.key}`)}</strong>
                <span className="num text-micro text-muted-foreground">+{aud(s.revenue)} · +{aud(s.gross_profit_known)}</span>
              </div>
              {row(s.cumulative_revenue - s.revenue, s.cumulative_revenue, "bg-primary", t("month.buildUpRevenue"))}
              <div className="mt-1">{row(s.cumulative_profit_known - s.gross_profit_known, s.cumulative_profit_known, "bg-success", t("month.buildUpProfit"), s.gross_profit_estimated ?? 0)}</div>
              {s.unpriced_count > 0 && <div className="mt-0.5 text-micro text-warning">{t("month.buildUpUnpriced", { count: s.unpriced_count })}</div>}
            </li>
          );
        })}
      </ol>
      <svg viewBox={`0 0 ${W} ${H}`} className="hidden h-auto w-full sm:block" role="img" aria-label={t("month.buildUpAria", { summary })}>
        <defs><pattern id="month-est-hatch" width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width={3} height={6} className="fill-success" /></pattern></defs>
        <line x1={0} x2={W} y1={BOTTOM} y2={BOTTOM} className="stroke-border" />
        {fixedCostTarget > 0 && (
          <line x1={0} x2={W} y1={y(fixedCostTarget)} y2={y(fixedCostTarget)} className="stroke-warning" strokeDasharray="4 4" />
        )}
        {steps.map((s, i) => {
          const x0 = i * slot + (slot - (BAR * 2 + GAP)) / 2;
          const prevRevenue = s.cumulative_revenue - s.revenue, prevProfit = s.cumulative_profit_known - s.gross_profit_known;
          const light = s.key === "opportunity";
          return (
            <g key={s.key}>
              {bar(x0, prevRevenue, s.cumulative_revenue, "fill-primary", light, `${t("month.buildUpRevenue")} +${aud(s.revenue)}`)}
              {bar(x0 + BAR + GAP, prevProfit, s.cumulative_profit_known, "fill-success", light, `${t("month.buildUpProfit")} +${aud(s.gross_profit_known)}`, s.gross_profit_estimated ?? 0)}
              <text x={i * slot + slot / 2} y={BOTTOM + 16} textAnchor="middle" className="fill-foreground" fontSize={11} fontWeight={600}>{i === 0 ? "" : "+ "}{t(`month.buildUp.${s.key}`)}</text>
              <text x={i * slot + slot / 2} y={BOTTOM + 31} textAnchor="middle" className="fill-muted-foreground num" fontSize={10}>+{aud(s.revenue)} · +{aud(s.gross_profit_known)}</text>
              {s.unpriced_count > 0 && <text x={i * slot + slot / 2} y={BOTTOM + 45} textAnchor="middle" className="fill-warning" fontSize={10}>{t("month.buildUpUnpriced", { count: s.unpriced_count })}</text>}
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-micro text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-primary" />{t("month.buildUpRevenue")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-success" />{t("month.buildUpProfit")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-muted" />{t("month.buildUpBase")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm text-success" style={{ backgroundImage: HATCH }} />{t("month.buildUpEstimated")}</span>
        {fixedCostTarget > 0 && <span className="inline-flex items-center gap-1.5"><span className="w-4 border-t-2 border-dashed border-warning" />{t("month.buildUpTarget", { amount: aud(fixedCostTarget) })}</span>}
        <span>{t("month.buildUpOpportunityNote")}</span>
      </div>
    </div>
  );
}
